import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Button, ButtonAppearance, Divider, Heading, Icon, Eyebrow, IconSize } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { throttle } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import { FilterSection, FilterOption } from './FilterSection';

const NAVBAR_HEIGHT = 56;
const BUTTON_GAP = 12;
const INITIAL_BUTTON_POSITION = 180;

export interface FilterSectionConfig {
  key: string; // Query param key (e.g., 'status', 'type', 'apps')
  title: string; // Display title (e.g., 'Status', 'Type', 'App')
  options: readonly (string | FilterOption)[]; // Filter options - supports both string and FilterOption
}

interface FilterItem {
  isSelected: boolean;
  isDisabled: boolean;
  icon?: ReactElement;
}

export interface SortConfig {
  items: Record<string, FilterItem>;
  currentSort: string; // Current sort value from URL
}

interface MobileFilterMenuProps {
  filterSections: FilterSectionConfig[];
  selectedFiltersLength: number;
  onApplyFilters: (filters: Record<string, string[]>, sortValue?: string) => void; // Callback to apply filters and sort to URL
  searchComponent?: ReactElement; // Optional search bar (for Catalog compatibility)
  auxiliaryComponent?: ReactElement; // Optional auxiliary component (for Catalog compatibility)
  sortConfig?: SortConfig; // Optional sort configuration
  onClearAll?: () => void;
}

const CountBadge = ({ count }: { count: number }): ReactElement => {
  return <p css={[noFiltersStyles, count && selectedFiltersCounterStyles]}>{count}</p>;
};

export const MobileFilterMenu = ({
  filterSections,
  selectedFiltersLength,
  onApplyFilters,
  onClearAll = () => {},
  searchComponent,
  auxiliaryComponent,
  sortConfig,
}: MobileFilterMenuProps): ReactElement => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [searchParams] = useSearchParams();

  // Local state for pending filter selections (not yet applied to URL)
  const [pendingFilters, setPendingFilters] = useState<Record<string, Set<string>>>({});
  // Local state for pending sort selection (not yet applied to URL)
  const [pendingSort, setPendingSort] = useState<string>('');

  const filterContainerRef = useRef<HTMLDivElement>(null);

  // Initialize pending filters and sort from URL params when menu opens
  useEffect(() => {
    if (isMenuOpen) {
      const initialFilters: Record<string, Set<string>> = {};

      filterSections.forEach((section) => {
        const param = searchParams.get(section.key);

        initialFilters[section.key] = param ? new Set(param.split(',')) : new Set();
      });
      setPendingFilters(initialFilters);

      // Initialize pending sort from sortConfig
      if (sortConfig) {
        setPendingSort(sortConfig.currentSort);
      }
    }
  }, [isMenuOpen, filterSections, searchParams, sortConfig]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isMenuOpen) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow;

      // Prevent scrolling
      document.body.style.overflow = 'hidden';

      // Cleanup: restore original overflow when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMenuOpen]);

  const handleSaveChangesClick = (): void => {
    // Convert Sets to arrays and apply to URL
    const filtersToApply: Record<string, string[]> = {};

    Object.entries(pendingFilters).forEach(([key, valueSet]) => {
      filtersToApply[key] = Array.from(valueSet);
    });

    // Apply filters and sort together in a single navigation
    onApplyFilters(filtersToApply, pendingSort || undefined);

    setIsMenuOpen(false);
  };

  const handleClearAllClick = (): void => {
    // Clear local state
    const clearedFilters: Record<string, Set<string>> = {};

    filterSections.forEach((section) => {
      clearedFilters[section.key] = new Set();
    });
    setPendingFilters(clearedFilters);

    // Reset sort to default if sortConfig exists
    if (sortConfig) {
      setPendingSort(sortConfig.currentSort);
    }

    onClearAll?.();
  };

  const handleSortClick = (sortValue: string): void => {
    setPendingSort(sortValue);
  };

  const handleFilterToggle = (sectionKey: string, value: string): void => {
    const currentSet = new Set(pendingFilters[sectionKey] || []);

    if (currentSet.has(value)) {
      currentSet.delete(value);
    } else {
      currentSet.add(value);
    }

    setPendingFilters({
      ...pendingFilters,
      [sectionKey]: currentSet,
    });
  };

  // Calculate selected count from pending filters
  const pendingSelectedCount = Object.values(pendingFilters).reduce((sum, set) => sum + set.size, 0);

  const handleExpand = (sectionKey: string): void => {
    setExpandedSection((prev) => (prev === sectionKey ? null : sectionKey));
  };

  useEffect(() => {
    const initialPosition = filterContainerRef.current?.getBoundingClientRect().top || INITIAL_BUTTON_POSITION;
    const offset = initialPosition - NAVBAR_HEIGHT;

    const handleScroll = throttle((): void => {
      setIsFixed(window.scrollY > offset);
    }, 0);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {!isMenuOpen ? (
        <div ref={filterContainerRef} css={[filterContainerStyles, isFixed && mobileFilterContainerScrolledStyles]}>
          {searchComponent && <div css={searchWrapperStyles}>{searchComponent}</div>}
          <Button
            data-testid="mobile-filter-button"
            label="Filters"
            appearance={ButtonAppearance.SECONDARY}
            iconAfter={
              selectedFiltersLength === 0 ? (
                <Icon size={IconSize.MEDIUM} type="filter" />
              ) : (
                <CountBadge count={selectedFiltersLength} />
              )
            }
            onClick={() => setIsMenuOpen(true)}
          />
          {auxiliaryComponent && <div css={auxiliaryComponentStyles}>{auxiliaryComponent}</div>}
        </div>
      ) : (
        <div css={containerStyles} data-testid="mobile-filter-menu">
          <div css={contentStyles}>
            <div
              css={css({
                width: '100%',
                '& > div:last-child': {
                  borderBottom: `2px solid ${token('color.border')}`,
                  paddingBottom: token('space.200'),
                },
              })}
            >
              <div css={crossIconContainerStyles}>
                <Button
                  label="Close"
                  iconBefore={<Icon type="cross" />}
                  appearance={ButtonAppearance.ICON}
                  onClick={() => setIsMenuOpen(false)}
                />
              </div>
              <div css={headingContainerStyles}>
                <Heading level={5}>Filter by:</Heading>
              </div>
              <div css={selectedCounterContainerStyles}>
                <p css={selectedTitleStyles}>Selected:</p>
                <CountBadge count={pendingSelectedCount} />
              </div>
              {sortConfig && (
                <div css={sortSectionContainerStyles}>
                  <div css={sectionDividerStyles}>
                    <Divider color={token('color.border')} height={2} />
                  </div>
                  <div
                    css={sortExpanderContainerStyles}
                    onClick={() => handleExpand('sort')}
                    data-testid="sort-section"
                  >
                    <Icon size={IconSize.LARGE} type={expandedSection === 'sort' ? 'chevron-down' : 'chevron-right'} />
                    <Eyebrow size="medium" fontWeight="semibold">
                      SORT BY
                    </Eyebrow>
                  </div>
                  {expandedSection === 'sort' && (
                    <div css={sortOptionsListStyles}>
                      {Object.entries(sortConfig.items).map(([label]) => (
                        <div
                          key={label}
                          css={[sortOptionStyles, pendingSort === label && sortOptionSelectedStyles]}
                          onClick={() => handleSortClick(label)}
                          data-testid={`sort-option-${label}`}
                        >
                          <span css={sortLabelStyles}>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {filterSections.map((section) => (
                <FilterSection
                  key={section.key}
                  title={section.title}
                  queryParamKey={section.key}
                  options={section.options}
                  isExpanded={expandedSection === section.key}
                  onToggleExpand={() => handleExpand(section.key)}
                  selectedValues={pendingFilters[section.key]}
                  onFilterToggle={(value) => handleFilterToggle(section.key, value)}
                />
              ))}
            </div>
            <div css={modalFooterStyles}>
              <div css={buttonsListStyles}>
                <Button label="Clear All" appearance={ButtonAppearance.LINK} onClick={handleClearAllClick} />
                <Button label="Save Changes" appearance={ButtonAppearance.PRIMARY} onClick={handleSaveChangesClick} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const containerStyles = css({
  paddingTop: token('space.200'),
});

const filterContainerStyles = css({
  display: 'flex',
  gap: token('space.150'),
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const searchWrapperStyles = css({
  flex: 1,
});

const mobileFilterContainerScrolledStyles = css({
  display: 'flex',
  position: 'fixed',
  top: NAVBAR_HEIGHT,
  left: 0,
  backgroundColor: token('color.background.input'),
  padding: `${BUTTON_GAP}px ${token('space.200')}`,
  width: '100%',
  maxWidth: '100vw',
  zIndex: 10,
});

const contentStyles = css({
  display: 'flex',
  width: '100%',
  position: 'fixed',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: token('space.200'),
  left: 0,
  top: '56px',
  bottom: 0,
  backgroundColor: token('color.background.input'),
  overflowY: 'auto',
  zIndex: 11,
  marginBottom: '74px', // buffer for footer
});

const crossIconContainerStyles = css({
  display: 'flex',
  justifyContent: 'end',
});

const headingContainerStyles = css({
  paddingBottom: token('space.200'),
});

const sectionDividerStyles = css({
  width: '100%',
  marginTop: token('space.200'),
  marginBottom: token('space.200'),
});

const sortSectionContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: token('space.200'),
});

const sortExpanderContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',

  // Eyebrow margin override
  '& > h4': {
    marginLeft: token('space.100'),
    marginTop: 0,
    marginBottom: 0,
  },
});

const sortOptionsListStyles = css({
  marginTop: token('space.150'),
  width: '100%',
});

const sortOptionStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.100'),
  padding: `${token('space.150')} ${token('space.100')}`,
  cursor: 'pointer',
  borderRadius: '4px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: token('color.text'),
  width: '100%',

  '&:hover': {
    backgroundColor: token('color.background.neutral.subtle.hovered'),
  },

  '&:last-child': {
    marginBottom: 0,
  },
});

const sortOptionSelectedStyles = css({
  color: token('color.text.selected'),

  '&:hover': {
    backgroundColor: token('color.background.selected.hovered'),
  },
});

const sortLabelStyles = css({
  flex: 1,
});

const selectedCounterContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const selectedTitleStyles = css({
  margin: 0,
  color: token('color.text.subtle'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '22px',
});

const noFiltersStyles = css({
  margin: `0 0 0 ${token('space.050')}`,
  padding: `${token('space.025')} ${token('space.075')}`,
  color: token('color.text'),
  fontFamily: 'SF Pro, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '12px',
  borderRadius: '8px',
  backgroundColor: token('color.background.neutral'),
});

const selectedFiltersCounterStyles = css({
  color: token('color.text.inverse'),
  backgroundColor: token('color.background.brand.bold'),
});

const buttonsListStyles = css({
  display: 'flex',
  paddingRight: token('space.300'),
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: token('space.200'),
  position: 'fixed',
  bottom: 0,
  left: 0,
  paddingBottom: token('space.200'),
  backgroundColor: token('color.background.input'),
  width: '100%',
  borderTop: `2px solid ${token('color.border')}`,
  paddingTop: token('space.200'),
});

const modalFooterStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});

const auxiliaryComponentStyles = css({
  alignSelf: 'flex-end',
});
